import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import Button from '../../components/Button';
import { UI_TEXT, getRandomAnalyzingMessage } from '../../config/ui-text';
import { SurveyAnalyzingIndicator } from './SurveyAnalyzingIndicator';
import { getNextDynamicQuestion, submitDynamicAnswer } from '../../utils/surveyApi';
import { AiDynamicSectionQuestionInput } from './AiDynamicSectionQuestionInput';
import type { VoiceOrTextTranscribeContext } from './VoiceOrTextInput';
import {
  buildAnswerPayload,
  isAnswerComplete,
  maxQuestionsFor,
  progressLabel,
  STOP_ERROR,
  type AiDynamicSectionQuestion,
  type DynamicSectionEmittedQuestion,
  type DynamicSectionStopReason,
} from './aiDynamicSection';

/**
 * `AiDynamicSectionQuestion` (from `aiDynamicSection.ts`, owned by a concurrent workstream) types
 * only the authoring config fields. `disable_voice_input` / `disable_text_input` are generic
 * per-question flags that live on the same authored row (`components/survey/types.ts::ParsedQuestion`)
 * one level up - `SurveySession.tsx` casts that row down to `AiDynamicSectionQuestion` at the
 * delegation seam, so the fields are present on the real object at runtime; this widens the local
 * TYPE to admit reading them, without touching the shared `aiDynamicSection.ts` file.
 */
export type AiDynamicSectionQuestionWithVoiceFlags = AiDynamicSectionQuestion & {
  disable_voice_input?: boolean;
  disable_text_input?: boolean;
};

export interface AiDynamicSectionRunnerProps {
  question: AiDynamicSectionQuestionWithVoiceFlags;
  sessionId: number;
  onSectionDone: (stopReason: DynamicSectionStopReason) => void;
}

const SLOT_SEPARATOR = '|';

/**
 * Drives one `ai_dynamic_section` end to end: fetches a turn, renders it with the input widgets
 * for that type, submits the answer, and loops until the server says the section is done.
 *
 * All state lives here, not in `SurveySession.tsx` (spec-01 + task brief: that file stays at its
 * delegation branch only). Every call goes through the `sessionFetch`-based wrappers in
 * `utils/surveyApi.ts` - never a bare `fetch()`, which would 404 for a real respondent.
 *
 * No stop_reason is ever shown to the respondent, including `'error'` (spec-01 edge case:
 * a prober failure must fail open and look like a normal section ending, never an error screen).
 *
 * An `open_text` turn's mic uses the SAME `VoiceOrTextInput` component `SurveySession.tsx` uses for
 * every other open-ended question (task-9: "shall work as other questions work in survey"), wired
 * through `AiDynamicSectionQuestionInput`. `showTextInput` / `transcriptionUnavailable` are owned
 * HERE, not inside that component, and are scoped to this one section rather than the whole
 * session - a respondent's channel choice (and a transcription failure) inside this section does
 * not follow them back out into the rest of the survey, or vice versa.
 */
export function AiDynamicSectionRunner({ question, sessionId, onSectionDone }: AiDynamicSectionRunnerProps) {
  const [turn, setTurn] = useState<DynamicSectionEmittedQuestion | null>(null);
  const [answer, setAnswer] = useState<unknown>(undefined);
  const [isBusy, setIsBusy] = useState(false);
  // Picked once per wait rather than per render, so the message does not flicker between frames.
  const [analyzingMessage, setAnalyzingMessage] = useState(getRandomAnalyzingMessage);
  const [showTextInput, setShowTextInput] = useState(false);
  const [transcriptionUnavailable, setTranscriptionUnavailable] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);

  // Synchronous re-entrancy latch for BOTH the "next question" and "submit answer" calls (task-9,
  // guard requirement). State (`isBusy`) is not enough on its own: two clicks in the same tick both
  // read the old `false` and both fire - a ref flips immediately, before the next render. Every
  // network step for this section (the initial fetch, and a submit's own follow-up fetch) runs
  // inside ONE guarded window, since a submit's fetchNext is not a separate user action.
  const inFlightRef = useRef(false);

  // Forces the input channel when the author disabled one for the section, or once transcription
  // has broken - mirrors SurveySession's own forcing effect (`disable_voice_input` /
  // `disable_text_input` override the respondent's prior choice; a broken transcription service
  // overrides everything, since typing is the only channel left). Re-evaluated on every turn so a
  // section-wide flag stays enforced across the whole probe, not just the first turn.
  const forcedChannelRef = useRef(false);
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect -- deriving which channel is forced off the
       author's flags (and the sticky transcription-failure kill switch) IS the synchronization
       this effect exists for; there is no external system to subscribe to instead. Mirrors
       SurveySession.tsx's own equivalent forcing effect. */
    if (transcriptionUnavailable) {
      setShowTextInput(true);
      return;
    }
    if (question.disable_voice_input) {
      setShowTextInput(true);
      forcedChannelRef.current = true;
    } else if (question.disable_text_input) {
      setShowTextInput(false);
      forcedChannelRef.current = true;
    } else if (forcedChannelRef.current) {
      setShowTextInput(false);
      forcedChannelRef.current = false;
    }
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [turn?.question_id, question.disable_voice_input, question.disable_text_input, transcriptionUnavailable]);

  const maxQuestions = maxQuestionsFor(question);

  const fetchNext = useCallback(async () => {
    setAnalyzingMessage(getRandomAnalyzingMessage());
    setIsBusy(true);
    try {
      const result = await getNextDynamicQuestion(sessionId, question.id);
      if ('done' in result) {
        onSectionDone(result.stop_reason);
        return;
      }
      setTurn(result.question);
      setAnswer(undefined);
      setVoiceError(null);
    } catch {
      // Fail open, matching the backend's own posture: a broken network call must never
      // strand the respondent on this question.
      onSectionDone(STOP_ERROR);
    } finally {
      setIsBusy(false);
    }
  }, [sessionId, question.id, onSectionDone]);

  useEffect(() => {
    // Runs once per section entry (keyed on the section question id) - fetchNext itself
    // re-fetches on every subsequent turn via handleSubmit, not via this effect re-firing.
    // Guarded by inFlightRef like every other trigger of fetchNext (task-9): a second mount of
    // this effect for the same section (StrictMode's double-invoke, or a rapid remount) must not
    // dispatch a second `/next` and spend a second LLM-generated question.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    fetchNext().finally(() => { inFlightRef.current = false; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id]);

  const handleSubmit = async () => {
    if (!turn || inFlightRef.current) return;
    inFlightRef.current = true;
    setAnalyzingMessage(getRandomAnalyzingMessage());
    setIsBusy(true);
    try {
      const payload = buildAnswerPayload(turn, answer);
      const result = await submitDynamicAnswer(sessionId, question.id, payload);
      if ('done' in result) {
        onSectionDone(result.stop_reason);
        return;
      }
      await fetchNext();
    } catch {
      onSectionDone(STOP_ERROR);
    } finally {
      setIsBusy(false);
      inFlightRef.current = false;
    }
  };

  // While a turn is being written — on first load AND between turns — show the SAME screen the
  // rest of the survey shows while the AI works. Previously the between-turns wait had no screen
  // at all, only a disabled button, and a respondent reported it as a freeze.
  if (!turn || isBusy) {
    return (
      <SurveyAnalyzingIndicator
        message={analyzingMessage}
        detail="Preparing your next question…"
      />
    );
  }

  const transcribeContext: VoiceOrTextTranscribeContext = {
    sessionId,
    questionId: turn.question_id,
  };

  return (
    <div className="space-y-6">
      <p className="text-xs uppercase tracking-wide text-gray-400">
        {progressLabel(turn.turn, maxQuestions)}
      </p>
      <p className="text-lg font-medium text-gray-900">{turn.text}</p>
      <AiDynamicSectionQuestionInput
        question={turn}
        answer={answer}
        onAnswerChange={setAnswer}
        voice={{
          showTextInput,
          onShowTextInputChange: setShowTextInput,
          transcriptionUnavailable,
          onTranscriptionUnavailable: () => setTranscriptionUnavailable(true),
          disableVoiceInput: !!question.disable_voice_input,
          disableTextInput: !!question.disable_text_input,
          slotKey: `${question.id}${SLOT_SEPARATOR}${turn.question_id}`,
          transcribeContext,
          onError: setVoiceError,
        }}
      />
      {voiceError && (
        <p className="text-sm text-red-600" role="alert">{voiceError}</p>
      )}
      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isBusy || !isAnswerComplete(turn, answer)}
          icon={<ArrowRight className="w-4 h-4" />}
        >
          {UI_TEXT.HUMAN_SURVEY.SESSION.BUTTONS.NEXT}
        </Button>
      </div>
    </div>
  );
}
